import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../lib/config';
import { listUsers, updateUserRole, deactivateUser, reactivateUser, type UserDto } from '../lib/users';
import { ROLE_LABELS, ROLE_COLORS, type UserRole } from '../lib/roles';
import {
  uploadFile, listFiles, deleteFile, getFileStreamUrl,
  importTrainings, importMilestoneTemplates,
  importEmployeeRoster, importMetricSnapshots,
  triggerDocumentParse, getDocumentParseStatus,
  DOCUMENT_ENTITY_TYPE_LABELS,
  type FileUploadDto, type ParsedDocumentData, type ParsedTraining, type ParsedMilestoneTemplate,
  type ParsedEmployee, type ParsedMetricSnapshot,
  type DocumentValidation,
} from '../lib/fileUpload';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin')({
  component: AdminPage,
});

function AdminPage() {
  return (
    <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
      <AdminContent />
    </ProtectedRoute>
  );
}

// ─── Entity type labels for documents ────────────────────────────────────────
// Imported from fileUpload.ts — single source of truth shared with the server

const ENTITY_TYPE_LABELS = DOCUMENT_ENTITY_TYPE_LABELS;
const ENTITY_TYPE_OPTIONS = Object.entries(ENTITY_TYPE_LABELS);

// ─── Document icon ────────────────────────────────────────────────────────────

function DocIcon({ mimeType }: { mimeType?: string }) {
  const isPdf = mimeType?.includes('pdf');
  const isDoc = mimeType?.includes('word') || mimeType?.includes('docx');
  const color = isPdf ? 'text-red-400' : isDoc ? 'text-blue-400' : 'text-gray-400';
  return (
    <svg className={`w-5 h-5 ${color} shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function formatBytes(bytes?: number) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Upload validation modal ─────────────────────────────────────────────────

function UploadValidationModal({
  fileName,
  validation,
  userSelectedType,
  onForceUpload,
  onSwitchToDetected,
  onCancel,
}: {
  fileName: string;
  validation: DocumentValidation;
  userSelectedType: string;
  onForceUpload: () => void;
  onSwitchToDetected: (detectedType: string) => void;
  onCancel: () => void;
}) {
  const isBlocked = !validation.isHRDocument && validation.confidence === 'high';
  const isMismatch = validation.matchesUserSelection === false && validation.confidence === 'high';
  const detectedLabel = validation.category ? (ENTITY_TYPE_LABELS[validation.category] ?? validation.category) : null;
  const selectedLabel = ENTITY_TYPE_LABELS[userSelectedType] ?? userSelectedType;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className={`flex items-start gap-3 px-5 py-4 border-b ${isBlocked ? 'bg-red-50' : 'bg-amber-50'}`}>
          <span className="text-xl shrink-0 mt-0.5">{isBlocked ? '⛔' : '⚠️'}</span>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {isBlocked ? 'Document may not be HR-related' : 'Document type mismatch'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{fileName}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-700 italic">"{validation.reason}"</p>

          {isBlocked && (
            <div className="text-xs text-gray-600 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              <p>This document doesn't appear to be an HR document. Uploading irrelevant files degrades AI quality across all features.</p>
              {detectedLabel && <p className="font-medium">AI detected: <span className="text-red-700">{detectedLabel}</span></p>}
            </div>
          )}

          {!isBlocked && isMismatch && detectedLabel && (
            <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">You selected:</span>
                <span className="font-medium text-gray-800">{selectedLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-amber-100 px-2 py-0.5 rounded text-amber-700">AI detected:</span>
                <span className="font-medium text-amber-800">{detectedLabel}</span>
                <span className="text-amber-600">({validation.confidence} confidence)</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`px-5 py-4 border-t flex flex-col gap-2 ${isBlocked ? 'bg-red-50/50' : 'bg-amber-50/50'}`}>
          {/* Mismatch — offer to switch type or keep user's choice */}
          {!isBlocked && isMismatch && validation.category && (
            <button
              type="button"
              onClick={() => onSwitchToDetected(validation.category!)}
              className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Use AI's detection — {detectedLabel}
            </button>
          )}
          {!isBlocked && (
            <button
              type="button"
              onClick={onForceUpload}
              className="w-full py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {isMismatch ? `Keep my selection — ${selectedLabel}` : 'Upload anyway'}
            </button>
          )}
          {/* Blocked — only "Upload anyway" or Cancel */}
          {isBlocked && (
            <button
              type="button"
              onClick={onForceUpload}
              className="w-full py-2 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
            >
              Upload anyway (override AI check)
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel upload
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Import preview modal ────────────────────────────────────────────────────

function ImportPreviewModal({
  companyId,
  parsedData,
  onClose,
  onImported,
}: {
  companyId: string;
  parsedData: ParsedDocumentData;
  onClose: () => void;
  onImported: () => void;
}) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ inserted: number; updated?: number; skipped: number; errors?: Array<{ row?: string; email?: string; reason: string }> } | null>(null);

  async function handleImport() {
    setImporting(true);
    try {
      if (parsedData.type === 'training_catalogue') {
        const res = await importTrainings(companyId, parsedData.trainings);
        if (res.success && res.data) {
          setResult({ inserted: res.data.inserted, updated: res.data.updated, skipped: res.data.skipped });
          toast.success(`Imported ${res.data.inserted} new training${res.data.inserted !== 1 ? 's' : ''}${res.data.updated ? `, updated ${res.data.updated}` : ''}.`);
          onImported();
        } else { toast.error(res.error ?? 'Import failed'); }
      } else if (parsedData.type === 'milestone_templates') {
        const res = await importMilestoneTemplates(companyId, parsedData.milestones);
        if (res.success && res.data) {
          setResult({ inserted: res.data.inserted, skipped: res.data.skipped });
          toast.success(`Imported ${res.data.inserted} milestone template${res.data.inserted !== 1 ? 's' : ''}.`);
          onImported();
        } else { toast.error(res.error ?? 'Import failed'); }
      } else if (parsedData.type === 'employee_roster') {
        const res = await importEmployeeRoster(companyId, parsedData.employees);
        if (res.success && res.data) {
          setResult({ inserted: res.data.created, skipped: res.data.skipped, errors: res.data.errors });
          toast.success(`Created ${res.data.created} employee account${res.data.created !== 1 ? 's' : ''}.`);
          onImported();
        } else { toast.error(res.error ?? 'Import failed'); }
      } else if (parsedData.type === 'metric_data') {
        const res = await importMetricSnapshots(companyId, parsedData.snapshots);
        if (res.success && res.data) {
          setResult({ inserted: res.data.created, skipped: res.data.skipped, errors: res.data.errors });
          toast.success(`Imported ${res.data.created} metric snapshot${res.data.created !== 1 ? 's' : ''}.`);
          onImported();
        } else { toast.error(res.error ?? 'Import failed'); }
      }
    } finally {
      setImporting(false);
    }
  }

  const typeLabels: Record<string, { title: string; item: string }> = {
    training_catalogue: { title: 'Import Training Catalogue', item: 'training program' },
    milestone_templates: { title: 'Import Milestone Templates', item: 'milestone template' },
    employee_roster: { title: 'Import Employee Roster', item: 'employee' },
    metric_data: { title: 'Import Metric Data', item: 'metric snapshot' },
  };
  const { title, item } = typeLabels[parsedData.type] ?? { title: 'Import', item: 'item' };

  const count =
    parsedData.type === 'training_catalogue' ? parsedData.trainings.length
    : parsedData.type === 'milestone_templates' ? parsedData.milestones.length
    : parsedData.type === 'employee_roster' ? parsedData.employees.length
    : parsedData.type === 'metric_data' ? parsedData.snapshots.length
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {count} {item}{count !== 1 ? 's' : ''} found — review and confirm import
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        {/* Preview list */}
        <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
          {result ? (
            <div className="py-6 text-center space-y-1">
              <p className="text-green-600 font-medium text-sm">Import complete</p>
              <p className="text-xs text-gray-500">
                {result.inserted} inserted{result.updated ? `, ${result.updated} updated` : ''}{result.skipped ? `, ${result.skipped} skipped` : ''}
              </p>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-3 text-left space-y-1">
                  <p className="text-xs font-medium text-red-600">Errors ({result.errors.length}):</p>
                  {result.errors.slice(0, 10).map((e, i) => (
                    <p key={i} className="text-xs text-red-500">{e.email ?? e.row}: {e.reason}</p>
                  ))}
                </div>
              )}
            </div>
          ) : parsedData.type === 'training_catalogue' ? (
            parsedData.trainings.map((t: ParsedTraining) => (
              <div key={t.code} className="border rounded-lg px-4 py-3 text-xs space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{t.code}</span>
                  <span className="font-medium text-gray-900">{t.name}</span>
                  <span className={`px-1.5 py-0.5 rounded ${t.format === 'online' ? 'bg-blue-50 text-blue-700' : t.format === 'offline' ? 'bg-purple-50 text-purple-700' : 'bg-teal-50 text-teal-700'}`}>{t.format}</span>
                  <span className="text-gray-400">{t.provider}</span>
                  {t.durationHours && <span className="text-gray-400">{t.durationHours}h</span>}
                </div>
                {t.description && <p className="text-gray-500 line-clamp-1">{t.description}</p>}
                {t.competenciesImproved.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {t.competenciesImproved.map((c) => (
                      <span key={c.competencyCode} className="bg-green-50 border border-green-200 text-green-700 px-1.5 py-0.5 rounded-full">
                        {c.competencyCode} +{c.levelGainEstimate}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : parsedData.type === 'milestone_templates' ? (
            parsedData.milestones.map((m: ParsedMilestoneTemplate, i: number) => (
              <div key={i} className="border rounded-lg px-4 py-3 text-xs space-y-1.5">
                <p className="font-medium text-gray-900">{m.name}</p>
                {m.milestone30 && <p className="text-gray-600"><span className="font-medium text-gray-800">30d:</span> {m.milestone30.slice(0, 120)}{m.milestone30.length > 120 ? '…' : ''}</p>}
                {m.milestone90 && <p className="text-gray-600"><span className="font-medium text-gray-800">90d:</span> {m.milestone90.slice(0, 120)}{m.milestone90.length > 120 ? '…' : ''}</p>}
                {m.milestone180 && <p className="text-gray-600"><span className="font-medium text-gray-800">180d:</span> {m.milestone180.slice(0, 120)}{m.milestone180.length > 120 ? '…' : ''}</p>}
              </div>
            ))
          ) : parsedData.type === 'employee_roster' ? (
            parsedData.employees.map((e: ParsedEmployee, i: number) => (
              <div key={i} className="border rounded-lg px-4 py-3 text-xs flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{e.name}</p>
                  <p className="text-gray-500">{e.email}</p>
                </div>
                <span className="shrink-0 text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded capitalize">{e.role}</span>
                {e.positionCode && <span className="shrink-0 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">{e.positionCode}</span>}
                {e.divisionName && <span className="shrink-0 text-xs text-gray-400">{e.divisionName}</span>}
              </div>
            ))
          ) : parsedData.type === 'metric_data' ? (
            parsedData.snapshots.map((s: ParsedMetricSnapshot, i: number) => (
              <div key={i} className="border rounded-lg px-4 py-3 text-xs flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{s.employeeEmail}</p>
                  {s.capturedAt && <p className="text-gray-400">{s.capturedAt}</p>}
                </div>
                <span className="shrink-0 font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s.metricCode}</span>
                <span className="shrink-0 font-semibold text-gray-800">{s.value}</span>
              </div>
            ))
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-3 justify-end shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">
            {result ? 'Close' : 'Skip'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              disabled={importing}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {importing ? 'Importing…' : `Import ${count} ${item}${count !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CSV template helpers ─────────────────────────────────────────────────────

function downloadCsvTemplate(type: 'roster' | 'metrics') {
  const templates = {
    roster: {
      filename: 'employee_roster_template.csv',
      content: 'name,email,role,positionCode,divisionName\nJohn Doe,john.doe@company.com,employee,SWE-L2,Engineering\nJane Smith,jane.smith@company.com,manager,MGR-001,Product',
    },
    metrics: {
      filename: 'metric_data_template.csv',
      content: 'employeeEmail,metricCode,value,capturedAt\njohn.doe@company.com,CODE_REVIEW_QUALITY,4.2,2024-01-15\njohn.doe@company.com,DELIVERY_RATE,85,2024-01-15',
    },
  };
  const { filename, content } = templates[type];
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Import tab ───────────────────────────────────────────────────────────────

function ImportTab({ companyId }: { companyId: string }) {
  const [subTab, setSubTab] = useState<'roster' | 'metrics'>('roster');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedDocumentData | null>(null);
  const [parseError, setParseError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);



  function resetState() {
    setFile(null);
    setParsedData(null);
    setParseError('');
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleParse() {
    if (!file) return;
    setParsing(true);
    setParseError('');
    setParsedData(null);

    try {
      // Upload the file as the appropriate entity type
      const entityType = subTab === 'roster' ? 'employee_roster' : 'metric_data';

      // Use the existing upload + parse pipeline
      const { uploadFile: upload, triggerDocumentParse: triggerParse, getDocumentParseStatus: getPollStatus } = await import('../lib/fileUpload');

      const uploadRes = await upload({ companyId, file, entityType, forceUpload: true });
      if (!uploadRes.success || !uploadRes.data) {
        setParseError(uploadRes.error ?? 'Upload failed');
        return;
      }

      const fileId = uploadRes.data.file.id;

      // Trigger parse
      await triggerParse(fileId, companyId);

      // Poll until done
      const MAX_POLLS = 30;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const status = await getPollStatus(fileId, companyId);
        if (!status.success || !status.data) continue;
        if (status.data.status === 'done') {
          if (status.data.parsedData) {
            setParsedData(status.data.parsedData);
            setShowModal(true);
          } else {
            setParseError('No structured data could be extracted from this file. Try a different format or check the column headers.');
          }
          return;
        }
        if (status.data.status === 'error') {
          setParseError(status.data.error ?? 'Parse failed');
          return;
        }
      }
      setParseError('Parse timed out. Try a smaller file.');
    } finally {
      setParsing(false);
    }
  }

  const subTabConfig = {
    roster: {
      label: 'Employee Roster',
      description: 'Upload a CSV, Excel, or document containing employee names, emails, roles, and departments. Accounts will be created with temporary passwords.',
      accept: '.csv,.xlsx,.xls,.pdf,.docx,.txt',
      templateType: 'roster' as const,
      exampleCols: ['name', 'email', 'role', 'positionCode', 'divisionName'],
    },
    metrics: {
      label: 'Metric Data',
      description: 'Upload a CSV, Excel, or document with employee metric values. Data will be imported as metric snapshots and automatically derive competency scores.',
      accept: '.csv,.xlsx,.xls,.pdf,.docx,.txt',
      templateType: 'metrics' as const,
      exampleCols: ['employeeEmail', 'metricCode', 'value', 'capturedAt'],
    },
  };
  const cfg = subTabConfig[subTab];

  return (
    <div className="space-y-5">
      {/* Sub-tab */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(['roster', 'metrics'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => { setSubTab(t); resetState(); }}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${subTab === t ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {subTabConfig[t].label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">{cfg.label} Import</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">{cfg.description}</p>
          </div>
          <button
            type="button"
            onClick={() => downloadCsvTemplate(cfg.templateType)}
            className="shrink-0 text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            ↓ CSV Template
          </button>
        </div>

        {/* Expected columns */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-gray-400">Expected columns:</span>
          {cfg.exampleCols.map((col) => (
            <span key={col} className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{col}</span>
          ))}
        </div>

        {/* File picker */}
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept={cfg.accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              setParsedData(null);
              setParseError('');
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Choose file
          </button>
          {file && <span className="text-xs text-gray-600 truncate max-w-xs">{file.name}</span>}
          {file && (
            <button type="button" onClick={resetState} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>

        {parseError && (
          <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{parseError}</p>
        )}

        {/* Action */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleParse}
            disabled={!file || parsing}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {parsing && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {parsing ? 'Extracting data…' : 'Extract & Preview'}
          </button>
          {parsedData && !showModal && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="px-4 py-2 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-medium"
            >
              Review extracted data
            </button>
          )}
        </div>

        {/* Info hint */}
        <p className="text-xs text-gray-400">
          AI will extract structured data from any format — CSV, Excel, PDF, or plain text. For best results, use the CSV template above.
        </p>
      </div>

      {showModal && parsedData && (
        <ImportPreviewModal
          companyId={companyId}
          parsedData={parsedData}
          onClose={() => setShowModal(false)}
          onImported={() => { setShowModal(false); resetState(); }}
        />
      )}
    </div>
  );
}

// ─── Documents tab ────────────────────────────────────────────────────────────

function DocumentsTab({ companyId, userId }: { companyId: string; userId?: string }) {
  const [docs, setDocs] = useState<FileUploadDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  // Upload state
  const [uploadFile_, setUploadFile_] = useState<File | null>(null);
  const [uploadEntityType, setUploadEntityType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  // Validation modal state — shown when AI detects an issue
  const [validationModal, setValidationModal] = useState<{
    validation: DocumentValidation;
    pendingEntityType: string; // entityType that will be used on override
  } | null>(null);

  // Parse & Import state
  // parsingFileId: the file currently being polled; importData: data ready to import
  const [parsingFileId, setParsingFileId] = useState<string | null>(null);
  const [importData, setImportData] = useState<ParsedDocumentData | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Delete state
  const [deleting, setDeleting] = useState<string | null>(null);

  function handleDownload(doc: FileUploadDto) {
    const url = getFileStreamUrl(doc.id, companyId);
    if (!url) { toast.error('Not authenticated'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const fetchDocs = useCallback(() => {
    setLoading(true);
    listFiles(companyId, filterType ? { entityType: filterType } : undefined).then((res) => {
      setLoading(false);
      if (res.success && res.data) setDocs(res.data.files);
    });
  }, [companyId, filterType]); // eslint-disable-line

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);

  async function doUpload(entityType: string, forceUpload?: boolean) {
    if (!uploadFile_) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    const res = await uploadFile({
      companyId, uploadedByUserId: userId, entityType, file: uploadFile_, forceUpload,
    });
    setUploading(false);

    if (!res.success) {
      // AI detected non-HR document (high confidence) — show modal
      if (res.code === 'not_hr_document' && res.validation) {
        setValidationModal({ validation: res.validation, pendingEntityType: entityType });
        return;
      }
      toast.error(res.error ?? 'Upload failed');
      setUploadError(res.error ?? 'Upload failed');
      return;
    }

    // Upload succeeded — check for validation warnings
    const validation = res.data?.file?.validation;
    if (validation && !forceUpload) {
      // Strong category mismatch (high confidence) — show modal
      if (!validation.matchesUserSelection && validation.confidence === 'high' && validation.category) {
        setValidationModal({ validation, pendingEntityType: entityType });
        // File is already uploaded at this point — inform user
        toast.info('File uploaded. Review the AI suggestion below.', { duration: 4000 });
        fetchDocs();
        return;
      }
      // Low/medium confidence mismatch — just show a toast
      if (!validation.matchesUserSelection && validation.category) {
        const detectedLabel = ENTITY_TYPE_LABELS[validation.category] ?? validation.category;
        const selectedLabel = ENTITY_TYPE_LABELS[entityType] ?? entityType;
        toast.info(
          `AI detected "${detectedLabel}" but "${selectedLabel}" was used. ` +
          `(${validation.confidence} confidence — ${validation.reason})`,
          { duration: 7000 },
        );
      }
      // Non-HR with low/medium confidence — warn but don't block
      if (!validation.isHRDocument && validation.confidence !== 'high') {
        toast.warning(`AI is unsure this is an HR document (${validation.confidence} confidence): ${validation.reason}`, { duration: 6000 });
      }
    }

    toast.success(`"${uploadFile_.name}" uploaded successfully.`);
    setUploadSuccess(`"${uploadFile_.name}" uploaded successfully.`);
    setUploadFile_(null);
    setUploadEntityType('');
    fetchDocs();
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile_ || !uploadEntityType) return;
    await doUpload(uploadEntityType);
  }

  async function handleDelete(fileId: string) {
    setDeleting(fileId);
    await deleteFile(fileId, companyId);
    setDeleting(null);
    setDocs((prev) => prev.filter((f) => f.id !== fileId));
  }

  // Start polling after triggering a parse
  function startPolling(fileId: string) {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setParsingFileId(fileId);

    // Update the doc's parseStatus in local state immediately
    setDocs((prev) => prev.map((d) => d.id === fileId ? { ...d, parseStatus: 'pending' } : d));

    pollIntervalRef.current = setInterval(async () => {
      const res = await getDocumentParseStatus(fileId, companyId);
      if (!res.success || !res.data) return;

      const { status, parsedData } = res.data;

      // Update local doc state with latest status
      setDocs((prev) => prev.map((d) =>
        d.id === fileId ? { ...d, parseStatus: status, parsedData: parsedData ?? undefined } : d,
      ));

      if (status === 'done') {
        clearInterval(pollIntervalRef.current!);
        setParsingFileId(null);
        if (parsedData) {
          const count = parsedData.type === 'training_catalogue'
            ? ((parsedData as any).trainings?.length ?? 0)
            : ((parsedData as any).milestones?.length ?? 0);
          if (count > 0) {
            setImportData(parsedData as ParsedDocumentData);
            toast.success(`AI extracted ${count} ${parsedData.type === 'training_catalogue' ? 'training program' : 'milestone template'}${count !== 1 ? 's' : ''} — review and import below.`);
          } else {
            toast.info('AI finished parsing but found no structured data in the document.');
          }
        } else {
          toast.info('AI finished parsing but found no structured data in the document.');
        }
      } else if (status === 'error') {
        clearInterval(pollIntervalRef.current!);
        setParsingFileId(null);
        toast.error(`AI parsing failed. Try again or check the document format.`);
      }
    }, 2000);
  }

  async function handleParse(doc: FileUploadDto) {
    const res = await triggerDocumentParse(doc.id, companyId);
    if (!res.success || !res.data?.queued) {
      toast.error(res.data?.reason ?? res.error ?? 'Could not start parsing');
      return;
    }
    toast.info('AI is extracting structured data from the document…', { duration: 4000 });
    startPolling(doc.id);
  }

  return (
    <div className="space-y-5">
      {/* Upload card */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Upload Company Document</h2>
        <form onSubmit={handleUpload} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
            <select
              required
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={uploadEntityType}
              onChange={(e) => setUploadEntityType(e.target.value)}
            >
              <option value="">— Select type —</option>
              {ENTITY_TYPE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">File</label>
            {uploadFile_ ? (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <DocIcon mimeType={uploadFile_.type} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{uploadFile_.name}</p>
                  <p className="text-xs text-gray-400">{formatBytes(uploadFile_.size)}</p>
                </div>
                <button type="button" onClick={() => setUploadFile_(null)} className="text-gray-400 hover:text-red-500 text-sm">✕</button>
              </div>
            ) : (
              <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-blue-300 hover:bg-gray-50 transition-colors">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="text-sm text-gray-500">Drop file here or <span className="text-blue-600 font-medium">browse</span></span>
                <span className="text-xs text-gray-400">PDF, DOCX, DOC, TXT · max 20 MB</span>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const ext = ('.' + f.name.split('.').pop()?.toLowerCase()) as string;
                      const allowed = ['.pdf', '.docx', '.doc', '.txt'];
                      if (!allowed.includes(ext)) {
                        toast.error(`File type "${ext}" is not supported. Upload PDF, DOCX, DOC, or TXT only.`);
                        e.target.value = '';
                        return;
                      }
                      setUploadFile_(f); setUploadError(''); setUploadSuccess('');
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>

          {uploading && (
            <div className="text-xs text-blue-600 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
              Uploading and extracting text…
            </div>
          )}
          {uploadError && <p className="text-xs text-red-600">{uploadError}</p>}
          {uploadSuccess && <p className="text-xs text-green-600">{uploadSuccess}</p>}

          <button
            type="submit"
            disabled={uploading || !uploadFile_ || !uploadEntityType}
            className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50 font-medium"
          >
            {uploading ? 'Uploading…' : 'Upload Document'}
          </button>
        </form>

        <p className="text-xs text-gray-400 mt-3">
          Uploaded documents are used as context in AI features (IDP generation, talent reports, and 9-box analysis).
          For Training Catalogue and milestone documents, use <strong>Parse &amp; Import</strong> to extract structured records.
        </p>
      </div>

      {/* Upload validation modal — shown when AI detects a problem with the uploaded document */}
      {validationModal && uploadFile_ && (
        <UploadValidationModal
          fileName={uploadFile_.name}
          validation={validationModal.validation}
          userSelectedType={validationModal.pendingEntityType}
          onForceUpload={() => {
            const entityType = validationModal.pendingEntityType;
            setValidationModal(null);
            doUpload(entityType, true);
          }}
          onSwitchToDetected={(detectedType) => {
            setValidationModal(null);
            setUploadEntityType(detectedType);
            doUpload(detectedType, false);
          }}
          onCancel={() => setValidationModal(null)}
        />
      )}

      {/* Import preview modal */}
      {importData && (
        <ImportPreviewModal
          companyId={companyId}
          parsedData={importData}
          onClose={() => setImportData(null)}
          onImported={() => { setImportData(null); fetchDocs(); }}
        />
      )}

      {/* Document list */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b gap-3">
          <h2 className="text-sm font-semibold text-gray-700">Company Documents</h2>
          <select
            className="border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All types</option>
            {ENTITY_TYPE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : docs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-400 text-sm">No documents uploaded yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {docs.map((doc) => {
              const isParseable = doc.isParseable && doc.hasExtractedText;
              const parseStatus = doc.parseStatus ?? 'idle';
              const isParsing = parsingFileId === doc.id || parseStatus === 'pending';
              const isDone = parseStatus === 'done';
              const isError = parseStatus === 'error';

              return (
                <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <DocIcon mimeType={doc.mimeType} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{doc.fileName}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-400">{formatBytes(doc.fileSize)}</span>
                      {doc.entityType && (
                        <span className="text-xs bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded">
                          {ENTITY_TYPE_LABELS[doc.entityType] ?? doc.entityType}
                        </span>
                      )}
                      {doc.hasExtractedText && (
                        <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded">Text extracted</span>
                      )}
                      {isDone && (
                        <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">AI parsed</span>
                      )}
                      {isError && (
                        <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded">Parse failed</span>
                      )}
                      <span className="text-xs text-gray-300">{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {/* Parse & Import button — only for parseable types with extracted text */}
                    {isParseable && !isDone && (
                      <button
                        type="button"
                        onClick={() => handleParse(doc)}
                        disabled={isParsing}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="AI will extract structured training or milestone data from this document"
                      >
                        {isParsing ? (
                          <>
                            <div className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                            Parsing…
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            Parse &amp; Import
                          </>
                        )}
                      </button>
                    )}
                    {/* Re-import button if already parsed */}
                    {isDone && doc.parsedData && (
                      <button
                        type="button"
                        onClick={() => setImportData(doc.parsedData as ParsedDocumentData)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-green-200 text-green-600 hover:bg-green-50 transition-colors"
                        title="View and re-import the previously parsed data"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Import
                      </button>
                    )}
                    {/* Download button */}
                    <button
                      type="button"
                      onClick={() => handleDownload(doc)}
                      className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                      title="Download file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(doc.id)}
                      disabled={deleting === doc.id}
                      className="text-gray-300 hover:text-red-500 disabled:opacity-40 transition-colors p-1"
                      title="Delete document"
                    >
                      {deleting === doc.id ? (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main admin content with tabs ─────────────────────────────────────────────

function AdminContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [tab, setTab] = useState<'users' | 'documents' | 'import'>('users');

  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [roleModal, setRoleModal] = useState<{ user: UserDto; newRole: UserRole } | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listUsers(companyId, {}).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) setUsers(res.data.users);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  async function handleRoleChange() {
    if (!roleModal) return;
    setRoleLoading(true);
    const res = await updateUserRole(roleModal.user.id, companyId, roleModal.newRole);
    setRoleLoading(false);
    if (res.success && res.data) {
      toast.success(`Role updated to ${roleModal.newRole}`);
      setUsers((prev) => prev.map((u) => (u.id === roleModal.user.id ? res.data!.user : u)));
      setRoleModal(null);
    } else {
      toast.error(res.error ?? 'Failed to update role');
    }
  }

  async function handleDeactivate(userId: string) {
    setDeactivating(userId);
    const res = await deactivateUser(userId, companyId);
    setDeactivating(null);
    if (res.success) {
      toast.success('User deactivated');
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: false } : u)));
    } else {
      toast.error(res.error ?? 'Failed to deactivate user');
    }
  }

  async function handleReactivate(userId: string) {
    setReactivating(userId);
    const res = await reactivateUser(userId, companyId);
    setReactivating(null);
    if (res.success) {
      toast.success('User reactivated');
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: true } : u)));
    } else {
      toast.error(res.error ?? 'Failed to reactivate user');
    }
  }

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const activeCount = users.filter((u) => u.isActive).length;
  const byRole = Object.entries(
    users.reduce<Record<string, number>>((acc, u) => {
      acc[u.role] = (acc[u.role] ?? 0) + 1;
      return acc;
    }, {})
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Admin</h1>
          <p className="text-sm text-gray-500 mt-1">User management, documents &amp; company settings</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{users.length}</p>
          <p className="text-xs text-gray-500 mt-1">Total Users</p>
        </div>
        <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          <p className="text-xs text-gray-500 mt-1">Active</p>
        </div>
        {byRole.slice(0, 2).map(([role, count]) => (
          <div key={role} className="bg-white rounded-xl border shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{count}</p>
            <p className="text-xs text-gray-500 mt-1 capitalize">{ROLE_LABELS[role as UserRole] ?? role}</p>
          </div>
        ))}
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-5 w-fit">
        <button
          type="button"
          onClick={() => setTab('users')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${tab === 'users' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Users
        </button>
        <button
          type="button"
          onClick={() => setTab('documents')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${tab === 'documents' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Documents
        </button>
        <button
          type="button"
          onClick={() => setTab('import')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${tab === 'import' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Import
        </button>
      </div>

      {/* Tab content */}
      {tab === 'import' ? (
        <ImportTab companyId={companyId} />
      ) : tab === 'users' ? (
        /* Users table */
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-semibold text-gray-700">All Users</h2>
            <input
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className="p-4 space-y-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase hidden sm:table-cell">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((u) => (
                    <tr key={u.id} className={`hover:bg-gray-50 ${!u.isActive ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                      <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role]}`}>
                          {ROLE_LABELS[u.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.id === user?.id ? (
                          <span className="text-xs text-gray-400">(you)</span>
                        ) : u.isActive ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setRoleModal({ user: u, newRole: u.role })}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Change Role
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeactivate(u.id)}
                              disabled={deactivating === u.id}
                              className="text-xs text-red-500 hover:underline disabled:opacity-40"
                            >
                              {deactivating === u.id ? '…' : 'Deactivate'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReactivate(u.id)}
                            disabled={reactivating === u.id}
                            className="text-xs text-green-600 hover:underline disabled:opacity-40"
                          >
                            {reactivating === u.id ? '…' : 'Reactivate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <DocumentsTab companyId={companyId} userId={user?.id} />
      )}

      {/* Role change modal */}
      {roleModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-semibold text-gray-800">Change Role</h2>
              <button type="button" onClick={() => setRoleModal(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Set role for <strong>{roleModal.user.name}</strong>:
              </p>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={roleModal.newRole}
                onChange={(e) => setRoleModal((r) => r ? { ...r, newRole: e.target.value as UserRole } : null)}
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="hr">HR</option>
                <option value="admin">Company Admin</option>
              </select>
              <div className="flex gap-3">
                <button type="button" onClick={() => setRoleModal(null)} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600">Cancel</button>
                <button type="button" onClick={handleRoleChange} disabled={roleLoading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
                  {roleLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
